import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  TBM,
  TBMQRSession,
  TBMAttendee,
  TBMPhoto,
  EducationSession,
  TBMSafetyCheck,
  getTBMStatusLabel,
  getTBMStatusBadgeTone,
  getTBMTagStyle,
  buildCriticalHazardMeasureSet,
  getExtraMeasures,
} from '../../lib/tbm';
import { isMultiSessionEducation } from '@safetyhub/shared';
import { isTbmEducationFinalized } from '../../pages/tbm/tbmEducationGates';
import { getFindingSeverityTone } from '../../lib/inspections';
import { resolveTbmEducationDoc } from '../../lib/tbmEducationDoc';
import type { TbmEducateAdapter } from './tbmEducateAdapter';
import {
  deriveSafetyCheckBase,
  findFirstUnselectedSafetyCheckIndex,
  findUnselectedSafetyChecks,
} from './safetyCheckBase';
import { EducationRecordSection } from './EducationRecordSection';
import QRModal from './QRModal';
import DirectSignModal from './DirectSignModal';
import { useLocale } from '../../lib/i18n/useLocale';
import { LanguageSwitcher } from '../i18n/LanguageSwitcher';
import { useDynamicTranslation } from '../../lib/i18n/useDynamicTranslation';
import { collectTbmDynamicTexts } from '../../lib/i18n/collectTbmDynamicTexts';
import { useToastStore } from '../../stores/toast';
import { Badge, Button, Card, Modal } from '@safetyhub/ui';

/**
 * 미선택 행 강조가 남아 있는 시간. 눈이 그 자리를 찾을 만큼만 — 계속 켜 두면 상주 문구
 * (「미선택 — …」)와 신호가 겹쳐 "고쳐도 안 지워지는 경고"처럼 보인다.
 */
const UNSELECTED_HIGHLIGHT_MS = 2500;

/**
 * 서버가 「그 회차는 이미 닫혔다」로 완료를 거절했을 때의 안내 (F-21,
 * `TBM_EDUCATION_SESSION_MISMATCH`).
 *
 * 어휘는 F-20(`viewingActiveSession` 안내)과 같은 가족이다 — 둘 다 「보고 있는 회차 ≠ 작용하는
 * 회차」를 말한다. 다른 점은 **누가 어긋냈나**뿐이다: F-20은 이 화면에서 지난 회차를 고른 것이고,
 * 이쪽은 다른 탭·담당자 링크가 그 사이 회차를 바꿔친 것이라 화면 혼자서는 알 수 없다.
 * 서버 기본 문구(`packages/shared` ko)는 「새로 고쳐 달라」인데, 화면은 그 일을 대신 할 수 있어
 * 여기서는 *이미 갱신했다*고 말한다.
 */
const SESSION_MISMATCH_NOTICE =
  '교육 회차가 그 사이 바뀌었습니다 — 보고 있던 회차는 이미 닫혔습니다. ' +
  '최신 상태로 갱신했으니 진행 중인 회차를 확인한 뒤 다시 완료해 주세요.';

interface TbmEducateViewProps {
  tbm: TBM;
  adapter: TbmEducateAdapter;
  /** 뒤로가기 핸들러 — 주어지면 헤더에 "← 상세" 버튼 노출 (안전관리자 인증 교육 화면용). 토큰 진입(educator)은 미전달. */
  onBack?: () => void;
  /** 로딩 UI와 함께 TBM 전체 재조회 (교육 완료 후 새 세션 복귀 등) */
  onReload: () => void;
  /** 스크롤 유지한 채 조용히 TBM 데이터 갱신 (교육 기록 편집 후) */
  onRefresh: () => void;
}

/**
 * TBM 교육 진행 뷰 (공유 컴포넌트).
 *
 * educator 페이지의 DirectEducatorView에서 추출한 뷰로, API 호출을
 * TbmEducateAdapter 인터페이스로 파라미터화했다.
 * educator 페이지는 토큰 기반 어댑터를 주입하고, EDU-2에서 안전관리자
 * 인증 페이지가 인증 기반 어댑터를 주입한다.
 */
export function TbmEducateView({
  tbm,
  adapter,
  onBack,
  onReload,
  onRefresh,
}: TbmEducateViewProps) {
  const { t, locale } = useLocale();
  const { showToast } = useToastStore();
  const [qrSession, setQrSession] = useState<TBMQRSession | null>(
    tbm.qrSession || null,
  );
  /**
   * 문서 상태는 prop을 그대로 읽는다 — 종전엔 `useState(tbm.status)` 사본을 두고 다회 기간종료 완료
   * 갈래가 `setTbmStatus('APPROVED')`로 덮어 썼다. 그 값은 **로컬 표시 전용**인데 이름이 진짜 문서
   * 상태 어휘와 같아, 아래 `=== 'APPROVED'` 분기들이 「결재 완료본」과 「방금 마지막 회차를 끝낸 화면」을
   * 구분할 수 없었다(라운드 4 사후 리뷰 INFO). 그 사실은 별도 불리언 `localCompleted`가 든다.
   */
  const tbmStatus = tbm.status;
  /**
   * 다회 교육의 마지막 회차를 **이 화면에서 방금** 완료했다 — 완료 후 토큰이 비활성화돼 재조회할 수
   * 없으므로 서버 문서 상태와 무관하게 로컬로만 「종료」를 표시한다. 서버 문서 상태(`tbm.status`)를
   * 바꾸는 것이 아니다. prop이 새 상태로 갱신되면(호스트가 재조회한 경우) 이 표시는 내린다.
   */
  const [localCompleted, setLocalCompleted] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [attendees, setAttendees] = useState<TBMAttendee[]>(tbm.attendees || []);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showDirectSign, setShowDirectSign] = useState(false);
  const [safetyCheckNotes, setSafetyCheckNotes] = useState('');
  const [completedSafetyCheckNotes, setCompletedSafetyCheckNotes] = useState('');
  // 다회차 회차별 "작업 전 안전조치 확인" — 교육담당자가 세션마다 입력
  const [safetyChecks, setSafetyChecks] = useState<TBMSafetyCheck[]>([]);
  const [completedSafetyChecks, setCompletedSafetyChecks] = useState<TBMSafetyCheck[] | null>(null);
  // 미선택 차단이 가리키는 행 — 스크롤과 강조를 **한 상태**가 몰고 간다(둘이 갈리면 강조만
  // 남거나 엉뚱한 행이 강조된다). 인덱스가 아니라 **객체**를 담는 이유는 재발동이다: 같은 행을
  // 다시 가리켜도 매번 새 객체라 effect가 다시 돌아 스크롤·강조가 되살아난다(숫자면 값이 같아
  // 두 번째 완료 시도에서 아무 일도 안 일어난다).
  const [unselectedFocus, setUnselectedFocus] = useState<{ idx: number } | null>(null);
  // 점검 행 DOM — 인덱스로 DOM을 뒤지지 않고 렌더가 직접 등록한다(목록이 바뀌어도 빗나가지 않게).
  const safetyCheckRowRefs = useRef<Array<HTMLDivElement | null>>([]);
  // 회차별 특이사항 — 교육담당자가 세션마다 입력 (다회차: session.specialNotes / 단일: content.specialNotes)
  const [specialNotes, setSpecialNotes] = useState('');
  const [completedSpecialNotes, setCompletedSpecialNotes] = useState('');
  // 세션 기록 저장(PATCH) 진행 상태
  const [isSavingRecords, setIsSavingRecords] = useState(false);
  const [recordsSavedAt, setRecordsSavedAt] = useState<number | null>(null);
  // 세션 기록 자동저장 dirty 플래그 — 사용자 편집 시에만 true.
  // init effect의 프로그램적 로드는 저장 트리거가 아니므로 false로 리셋한다.
  const recordsDirtyRef = useRef(false);

  const educatorName = adapter.educatorName;

  // 다회 교육 세션 파생 값. 판정 축은 공유 술어(end 날짜 > start 날짜 — 결정 2026-09-07).
  const isMultiSession = isMultiSessionEducation(tbm);
  // 기간 **범위** 판정(다회차 판정이 아니다). 같은 날은 새 계약에서 단일회차이므로 여기서도
  // false가 되는 것이 맞다 — 이 플래그가 여는 것은 회차 재진행·완료 후 편집 같은 다회차
  // 전용 동작이고, 리마인더 크론(start < 오늘 ≤ end)도 같은 날엔 원래 도달하지 못한다.
  //
  // ⚠️ **같은 날은 여기서 동작이 바뀐다 — 회귀가 아니다.** 종료일이 그 날 UTC 자정이라 KST
  // 근무시간이면 이 값은 구 축에서도 이미 false였다. 그래서 같은 날 문서는 *다회차인데 기간이
  // 이미 끝난* 취급을 받아 handleComplete의 "다회 기간종료: 완료 상태로 고정" 갈래와 「교육
  // 기간이 종료되었습니다」 잠금을 탔다. 새 축에서는 `!isMultiSession` 갈래(세션 완료 토스트 +
  // 상세 복귀 / 재시작 안내)로 간다 — "그 날 하루 교육"의 옳은 결말이다.
  // 회귀 spec: `__tests__/TbmEducateView.restartPolicy.test.tsx`의 같은 날 ↔ 여러 날 짝.
  const isWithinEducationPeriod = isMultiSession &&
    new Date() <= new Date(tbm.educationEndDate!);

  // 문서 읽기의 단일 파생 — 구(TBMContent)/신(COMPOSED) 두 형상을 한 형태로 수렴시킨다.
  // 이 화면은 `tbm.content`를 직접 읽지 않는다(형상이 늘면 놓치는 자리가 생긴다 —
  // 결정 docs/decisions/2026-09-08-composed-education-runtime.md).
  const doc = useMemo(() => resolveTbmEducationDoc(tbm), [tbm]);

  // ── 교육 기록(안전조치 확인·시행 결과·특이사항)의 저장 축은 **세션(EducationSession) 하나**다.
  //   단일회차든 다회차든 그 값들은 content에 없고 템플릿이 `tbmSessions-src`로 EducationSession에서
  //   읽는다. content 축이나 「내용 수정」 진입을 두지 않는다.
  //   이 파일의 `isMultiSession`은 *「여러 회차인가」*만 묻는다(교육 기간 판정 · 완료 후 네비 ·
  //   참석자/교육기록 세션 필터 · 라이브 폴링 · 활성 세션 추적 · 회차 선택 카드 · placeholder 문구).

  // 회차별 안전조치 확인의 위험요인 기준 목록 — 도출 규칙은 deriveSafetyCheckBase가 정본
  // (점검 항목만 깔고 조치 여부는 미체크로 시작한다. 이유는 그 파일 주석 참조).
  const safetyCheckBase = useMemo<TBMSafetyCheck[]>(() => deriveSafetyCheckBase(doc), [doc]);

  const todayStr = new Date().toISOString().split('T')[0];
  const hasTodaySession = (tbm.educationSessions || []).some(
    (s: EducationSession) => s.sessionDate.split('T')[0] === todayStr,
  );
  const hasTodayInProgressSession = (tbm.educationSessions || []).some(
    (s: EducationSession) => s.sessionDate.split('T')[0] === todayStr && s.status === 'IN_PROGRESS',
  );

  // tbm prop의 문서 상태가 바뀌면 로컬 「종료」 표시는 내린다(종전 `setTbmStatus(tbm.status)` 동기화와
  // 같은 시점 — 호스트가 재조회해 진짜 상태가 왔으면 그쪽이 정본이다).
  useEffect(() => {
    setLocalCompleted(false);
  }, [tbm.status]);

  /**
   * tbm prop 갱신 시 **QR 세션** 동기화 (F-39, 2026-09-12 흐름 7 실측).
   *
   * 종전에는 `useState(tbm.qrSession || null)`로 **초기값만** 읽었다. 그래서 담당자(B)가 토큰
   * 화면에서 교육을 시작·완료해도 — 서버가 `emitEducationChanged`로 TBM_UPDATED를 쏘고 호스트가
   * 조용히 재조회해 `tbm`이 갱신되는데도 — A(안전관리자)의 교육 화면은 **옛 값**을 본 채였다.
   * 증상은 양방향이다: B가 완료했는데 「교육 완료 (담당자 서명)」·「직접 서명 받기」가 남고
   * (`qrSession && !isFinalized`), B가 시작했는데 「교육 시작」이 남는다(`showStartButton`의
   * `!qrSession`). 상태·참석자는 이미 동기화 effect가 있었다(위/아래) — 이 축만 비어 있었다.
   *
   * ⚠️ 비교는 **객체가 아니라 식별자**로 한다. 5초 폴링·WS 재조회마다 같은 QR 세션이 새 객체
   * identity로 오는데, 객체 기준으로 set하면 아래 폴링 effect(deps `[qrSession]`)가 매번
   * 재시작돼 즉시 1회 폴링이 계속 덧붙는다. 함수형 setState가 키가 같으면 `prev`를 그대로
   * 돌려주므로 상태가 바뀌지 않고 폴링도 끊기지 않는다.
   * ⚠️ 키가 `id`가 아니라 **`id || token`**인 이유: 타입(`TBMQRSession`)은 `id`를 필수로 두지만
   * 실제로 그 자리에 오는 형상 중엔 **토큰만 있는 것**이 있다(예: 기존 spec 픽스처
   * `TbmEducateView.completeGate.test.tsx`의 `qrSession: { token, expiresAt }`). `id`만 보면 그런
   * 「있음」이 키 `null`로 잡혀 **부재와 구분되지 않고**, 그러면 완료·직접서명 버튼이 서 있는
   * 채로 동기화가 잠자거나 반대로 멀쩡한 세션을 지운다.
   *
   * ⚠️ deps에 **로컬 `qrSession`을 넣지 않는다** — `handleStartEducation`이 로컬을 새 세션으로
   * 먼저 바꾼 뒤 늦게 도착한 옛 응답(qrSession 부재)이 그것을 되돌리면 안 된다. dep이
   * `incomingQrSession` 하나면 그 경우는 「부재 → 부재」라 effect가 아예 돌지 않는다.
   */
  const incomingQrSession = tbm.qrSession ?? null;
  useEffect(() => {
    const keyOf = (s: TBMQRSession | null) => (s ? s.id || s.token : null);
    setQrSession((prev) => (keyOf(prev) === keyOf(incomingQrSession) ? prev : incomingQrSession));
  }, [incomingQrSession]);

  // 실시간 서명 현황 폴링 (QR 세션 활성 시 5초 간격)
  const pollAttendees = useCallback(async () => {
    try {
      const result = await adapter.refreshTbm();
      setAttendees(result.attendees || []);
    } catch {
      // 폴링 실패는 무시
    }
  }, [adapter]);

  // ⚠️ 구 조건은 `qrSession && tbmStatus === 'IN_PROGRESS'`였다. 뒤 항은 서버가 세션 시작 때
  // 문서를 `SIGNING`으로 올려 주던 것에 기대고 있었는데, 그 상태가 문서 enum에서 사라졌다
  // (2026-09-08 `tbm-status-model-two-axes`). QR 세션이 곧 「진행 중」이라 남은 항이 정본이다.
  useEffect(() => {
    if (qrSession) {
      // 즉시 1회 + 5초 간격 폴링
      pollAttendees();
      pollingRef.current = setInterval(pollAttendees, 5000);
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [qrSession, pollAttendees]);

  // tbm prop 갱신 시 attendees 동기화
  useEffect(() => {
    setAttendees(tbm.attendees || []);
  }, [tbm.attendees]);

  // 입력 단계 진입 시(QR 세션 활성) 회차별 안전조치 확인이 비어 있으면 기준 목록으로 초기화
  // (페이지 재진입으로 handleStartEducation을 거치지 않은 경우 대비)
  useEffect(() => {
    if (qrSession && safetyChecks.length === 0) {
      setSafetyChecks(safetyCheckBase);
    }
  }, [qrSession, safetyChecks.length, safetyCheckBase]);

  // (세션 기준 init effect가 유일한 초기화 경로다 — content 축 init을 두지 않는다.)

  // (⚠️ 「내용 수정」 편집기를 이 화면에 두지 않는다 — 저장이 content를 통째로 덮어써 -src 행·바인딩
  //  값이 사라진다. composed 문서의 편집은 작성 화면 소관이다(결정 2026-09-08).)

  const handleStartEducation = async () => {
    /**
     * 지금 화면에 올라와 있는 입력이 **끝난 회차의 기록**인가 (F-42, 2026-09-12 사용자 실측:
     * 「교육 시작 시에 이전 세션의 입력 내용이 그대로 있는데 초기화 해야 하지 않나?」).
     *
     * 아래 carry-over(시작 전 입력 보존)는 **시작 «전에» 담당자가 친 것**을 지키려는 장치인데,
     * staging의 출처가 직전 COMPLETED 회차이면(완료 뒤 그 회차를 보고 있는 상태 — init effect가
     * 그 회차 저장값을 실어 놓았다) 그 값이 새 회차로 흘러들어 자동저장이 그대로 새 회차에 쓴다.
     *
     * 가르는 축은 **세션 상태**다. 서버가 시작 시 재사용하는 세션은 `IN_PROGRESS`뿐이고
     * (`createQRSession`·`startEducationByToken` 둘 다 `findFirst({status:'IN_PROGRESS'})`),
     * 시작 전 lazy 생성(`upsertActiveSessionRecordsByToken`)도 `IN_PROGRESS`로 만든다. 즉
     * COMPLETED를 보고 있었다면 시작은 **반드시 새 회차**이고, 그 밖(세션 없음·IN_PROGRESS)이면
     * **그 세션이 그대로 이어져** 지우면 이미 저장된 값을 잃는다.
     *
     * `recordsDirtyRef`로 가르지 않는 이유: 완료 회차 값 위에 한두 글자 고친 것을 「새 회차
     * 입력」으로 볼 근거가 없다.
     *
     * ── 이 한 자리가 세 경우를 다 덮는 근거 ──────────────────────────────
     *  • **다회차도 같은 증상이다.** 회차 전환 init effect가 막아 줄 것 같지만 막지 못한다 —
     *    바로 위에서 `initializedSessionIdRef`를 선갱신하므로, 활성 세션 추적이 새 회차로
     *    전환해도 그 effect는 가드(`current === ref`)에 걸려 skip한다. 그래서 처방을 여기
     *    한 자리에 두면 단일·다회차가 함께 닫힌다.
     *  • **안전관리자(A) 인증 화면도 같다.** 같은 컴포넌트이고 `distributeTBM → createQRSession`
     *    이라 세션 재사용 정책이 토큰 경로와 같다. 다만 A에는 `upsertSessionRecords` 어댑터가
     *    없어 시작 전 입력이 서버에 없고, 그때는 `currentSession`이 대개 `undefined`라 보존
     *    갈래로 떨어진다.
     *
     * ⚠️ await **전에** 잡는다 — 시작 응답이 오는 사이 회차 목록·선택이 바뀔 수 있고, 물어야
     * 할 것은 「누를 때 보고 있던 회차」다.
     */
    const stagingCameFromCompletedSession = currentSession?.status === 'COMPLETED';

    setIsStarting(true);
    setError('');
    try {
      const session = await adapter.startEducation();
      // 시작 전 입력(안전조치 확인/시행 결과/특이사항)을 보존한다.
      // ref를 먼저 새 세션 id로 갱신해 init effect의 가드(current===ref)에 걸려
      // 빈 새 세션 값으로 로컬 staging이 덮어쓰이지 않게 한다.
      const newId = session.educationSessionId || null;
      initializedSessionIdRef.current = newId;
      setCurrentEducationSessionId(newId);
      if (stagingCameFromCompletedSession) {
        // 새 회차는 빈 기록으로 시작한다 — 안전조치 확인만 기준 목록(예/아니오 미체크)으로.
        // init effect는 위 ref 선갱신 때문에 돌지 않으므로 여기서 직접 되돌린다.
        setSafetyChecks(safetyCheckBase);
        setSafetyCheckNotes('');
        setSpecialNotes('');
        // 프로그램적 초기화는 자동저장 트리거가 아니다(빈 값을 새 회차에 즉시 쓰지 않는다).
        recordsDirtyRef.current = false;
        setRecordsSavedAt(null);
      }
      // 교육 시작은 이제 문서 상태를 바꾸지 않는다(구 `setTbmStatus('IN_PROGRESS')` 제거 —
      // 2026-09-08 `tbm-status-model-two-axes`). 「진행 중」 화면 전환은 아래 qrSession이 낸다.
      setQrSession(session as TBMQRSession);
      setShowQR(true); // QR 모달 자동 오픈
    } catch (err) {
      const { apiErrorMessage } = await import('../../lib/api');
      setError(apiErrorMessage(err) ?? '교육 시작에 실패했습니다.');
    } finally {
      setIsStarting(false);
    }
  };

  /**
   * 미선택 안전조치가 남아 있으면 완료를 막는다 — **그 항목으로 데려가고** false를 돌려준다.
   *
   * 자동저장은 실패를 삼키지만(catch {}) 이 차단은 그러면 안 된다: 완료가 조용히 안 되면
   * 교육담당자는 왜 안 되는지 모른 채 같은 버튼만 누른다. 서버(complete·completeByEducatorToken)도
   * 같은 판정을 한다.
   *
   * ⚠️ 안내를 **세 자리에 나눠** 둔다 — 종전에는 한 문단(무엇이·몇 개·어떻게)을 배너와 토스트에
   * 똑같이 넣어 길기만 했고, 정작 그 항목이 화면 어디 있는지는 사용자가 손으로 찾아야 했다.
   *  - 토스트: **한 줄**로 "왜 안 넘어갔나"만(항목명 없음 — 목록이 길면 토스트가 화면을 덮는다)
   *  - 스크롤·강조: "어디인가"
   *  - 그 행 옆 상주 문구: "어떻게 하나"(조치를 못 했으면 '아니오')
   * 배너(`setError`)는 이 경로에서 쓰지 않는다. 화면 위쪽에 있어 스크롤한 뒤엔 보이지 않고,
   * 이유는 이제 행 옆에 **상주**하므로(토스트는 4초 뒤 사라진다) 배너가 마지막 보루도 아니다.
   * `setError`는 API 실패 등 자리가 없는 오류만 계속 쓴다.
   */
  const blockOnUnselectedSafetyChecks = (): boolean => {
    const unselected = findUnselectedSafetyChecks(safetyChecks);
    if (unselected.length === 0) return false;
    showToast(`조치 여부를 고르지 않은 항목이 ${unselected.length}개 있습니다.`, 'error');
    const firstIdx = findFirstUnselectedSafetyCheckIndex(safetyChecks);
    if (firstIdx >= 0) setUnselectedFocus({ idx: firstIdx });
    setShowSignature(false);
    return true;
  };

  /**
   * 차단이 가리킨 행으로 스크롤하고 잠시 강조한다.
   *
   * effect로 미루는 이유는 서명 모달 때문이다 — 차단은 모달을 닫으면서 일어나는데, 모달이
   * 아직 붙어 있는 동안(스크롤 잠금) 스크롤하면 아무 데도 가지 않는다. effect는 그 언마운트가
   * 커밋된 뒤에 돈다.
   */
  useEffect(() => {
    if (!unselectedFocus) return;
    // jsdom엔 구현이 없다(선택 호출) — 실브라우저에서만 실제로 움직인다.
    safetyCheckRowRefs.current[unselectedFocus.idx]?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });
    const timer = setTimeout(() => setUnselectedFocus(null), UNSELECTED_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [unselectedFocus]);

  const handleComplete = async (signatureData?: string) => {
    if (blockOnUnselectedSafetyChecks()) return;
    setIsCompleting(true);
    setError('');
    try {
      await adapter.complete({
        signatureMethod: signatureData ? 'DRAWN' : 'NAME_INPUT',
        signatureData,
        // 닫으려는 회차를 서버에 말한다(F-21) — 화면 가드(F-20 viewingActiveSession)는 이 화면
        // 안의 어긋남만 막고, 다른 탭·담당자 링크가 그 사이 회차를 바꿔친 것은 서버만 안다.
        // 대상을 특정할 수 없으면 비운다(아래 targetSessionId 주석) → 종전 동작.
        sessionId: targetSessionId,
        // 세션 기록을 complete에 전달(서버가 EducationSession에 저장).
        safetyCheckNotes: safetyCheckNotes || undefined,
        safetyChecks: safetyChecks.length > 0 ? safetyChecks : undefined,
        specialNotes: specialNotes || undefined,
      });
      setShowSignature(false);
      // 폴링 중지
      if (pollingRef.current) clearInterval(pollingRef.current);

      if (isMultiSession && isWithinEducationPeriod) {
        // 다회 교육: 세션 완료 후 세션 목록으로 복귀
        // 서버에서 TBM 상태가 DRAFT로 복원되므로 데이터 재조회
        setQrSession(null);
        onReload();
      } else if (!isMultiSession) {
        // 단일회차: 세션 완료 안내 + 상세로 이동 (주 액션은 결재/완료 — 재시작은 보조 경로)
        // (완료/제출은 작성자가 상세에서 직접 — 서버 상태 전이는 유지)
        //
        // ⚠️ F-41: 종전에는 `showToast + onBack?.()`가 전부라, **`onBack`이 없는 호스트**
        // (교육담당자 토큰 화면 `pages/tbm/educator/[token].tsx`)에서는 완료가 화면에 아무것도
        // 남기지 않았다. 로컬 `qrSession`이 그대로라 버튼 게이트(`qrSession && !isFinalized`)가
        // 계속 참이어서 「직접 서명 받기」·「교육 완료 (담당자 서명)」이 서 있고, 그 아래엔
        // 「교육이 완료되었습니다…」 안내가 **동시에** 떠 있었다(2026-09-12 사용자 실측).
        // 완료는 호스트와 무관한 사실이므로 로컬 세션은 어느 경로든 내린다.
        setQrSession(null);
        showToast('교육 세션이 완료되었습니다.', 'success');
        if (onBack) {
          // 상세가 있는 호스트(안전관리자 인증 화면)는 그리로 — 재조회는 그 화면이 한다.
          onBack();
        } else {
          // 토큰 화면은 갈 곳이 없다. `tbm.educationSessions`에 COMPLETED가 실려야
          // `isSingleCompleted`가 참이 되어 완료 안내와 「교육 시작」(재시작)이 선다.
          // `onReload`가 아니라 `onRefresh`인 이유는 F-40 — 스피너가 화면을 갈아 끼우면
          // 방금 완료한 화면이 통째로 깜빡인다.
          onRefresh();
        }
      } else {
        // 다회 기간종료: 로컬 「종료」 표시(서버 문서 상태를 바꾸는 것이 아니다 — `localCompleted` JSDoc).
        // 완료 후 토큰이 비활성화되므로 onReload() 대신 로컬 상태만 갱신.
        setLocalCompleted(true);
        // 안전점검 결과를 로컬 세션 데이터로 보존 (토큰 비활성화로 재조회 불가)
        setCompletedSafetyCheckNotes(safetyCheckNotes);
        setCompletedSpecialNotes(specialNotes);
        if (safetyChecks.length > 0) {
          setCompletedSafetyChecks(safetyChecks);
        }
      }
    } catch (err) {
      // code 분기가 필요한 자리라 `extractApiError`를 남긴다 — 문구만 정본 헬퍼로(리뷰 W3).
      const { apiErrorMessage, extractApiError } = await import('../../lib/api');
      if (extractApiError(err).code === 'TBM_EDUCATION_SESSION_MISMATCH') {
        // 대상 회차가 그 사이 바뀌었다(F-21). 서버 기본 문구는 「새로 고쳐 확인하라」인데,
        // 여기서는 화면이 대신 당겨 올 수 있으므로 **그 일을 하고** 그렇게 말한다 —
        // 사용자에게 남는 할 일은 「어느 회차인지 보고 다시 누르기」뿐이다.
        onRefresh();
        setError(SESSION_MISMATCH_NOTICE);
      } else {
        setError(apiErrorMessage(err) ?? '교육 완료 처리에 실패했습니다.');
      }
    } finally {
      setIsCompleting(false);
    }
  };

  const updateSafetyCheck = (idx: number, field: keyof TBMSafetyCheck, value: boolean | string) => {
    setSafetyChecks((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
    setRecordsSavedAt(null);
    recordsDirtyRef.current = true;
  };

  // (⚠️ 기록을 content로 PATCH하는 경로를 두지 않는다 — 기록의 정본은 EducationSession이고,
  //  content에 쓰면 템플릿(tbmSessions-src)이 읽지 못해 문서에 안 보인다.)

  // 세션 기록(안전조치 확인/시행 결과/특이사항) 상시 저장 (PATCH) — 대상 세션이 존재할 때만
  // updateSessionRecords 호출
  const handleSaveRecords = async () => {
    setIsSavingRecords(true);
    setError('');
    try {
      if (!adapter.updateSessionRecords || !currentEducationSessionId) return;
      await adapter.updateSessionRecords(currentEducationSessionId, {
        safetyChecks: safetyChecks.length > 0 ? safetyChecks : undefined,
        safetyCheckNotes,
        specialNotes,
      });
      setRecordsSavedAt(Date.now());
      onRefresh();
    } catch (err) {
      const { apiErrorMessage } = await import('../../lib/api');
      setError(apiErrorMessage(err) ?? '교육 기록 저장에 실패했습니다.');
    } finally {
      setIsSavingRecords(false);
    }
  };

  const signUrl = qrSession
    ? `${window.location.origin}/educations/tbm/sign/${qrSession.token}?via=qr`
    : '';

  // 다회 교육 시 현재/선택된 세션 ID
  const [currentEducationSessionId, setCurrentEducationSessionId] = useState<string | null>(() => {
    const sessions = tbm.educationSessions || [];
    // IN_PROGRESS 세션 우선
    const inProgress = sessions.find((s: EducationSession) => s.status === 'IN_PROGRESS');
    if (inProgress) return inProgress.id;
    // 없으면 가장 최신 세션 (sessionDate desc 정렬이므로 첫 번째)
    if (sessions.length > 0) return sessions[0].id;
    return null;
  });
  /**
   * 이 회차의 참석자 — 헤더 `n/m`·서명 현황 카드·F-37 완료 게이트(`signedCount === 0`)가 읽는다.
   *
   * 축은 **회차**다 — 회차 수와 무관하게 늘 회차로 센다. 서명 행은 단일회차도 `sessionId`를
   * 갖는다(서버 `signByToken`이 활성 세션을 싣는다). 종전 `!isMultiSession → attendees 전체`는
   * 단일회차를 **완료 → 재시작**(2026-09-03 정책)한 뒤 지난 회차 서명자를 새 회차에 그대로 세어,
   * 헤더가 `3/3`이 되고 아무도 서명하지 않은 새 회차의 완료 게이트가 풀렸다(리뷰 C4, 2026-09-14).
   *
   * 고른 회차가 아직 없으면(교육 시작 직후 — QR은 섰는데 `educationSessions`에 그 회차가 아직
   * 안 실린 refetch 대기) **QR 세션이 가리키는 회차**로 센다. QR 세션은 진행 중 회차의 것이라
   * 그 id로 들어온 서명이 곧 이 회차의 서명이다. 이 폴백이 없으면 그 창에서 방금 받은 서명이
   * 화면에서 사라지고 완료 게이트(F-37)가 잠긴다.
   */
  const rosterSessionId = currentEducationSessionId ?? qrSession?.educationSessionId ?? null;
  const currentSessionAttendees = useMemo(() => {
    if (!rosterSessionId) return [];
    // sessionId로 정확하게 필터링
    return attendees.filter((a: TBMAttendee) => a.sessionId === rosterSessionId);
  }, [attendees, rosterSessionId]);

  const signedCount = currentSessionAttendees.filter(a => a.signedAt).length;
  const totalAttendees = currentSessionAttendees.length;

  // 현재 선택/대상 세션 — 완료 세션 열람·편집 시 저장값 표시용. 단일회차도 여기서 읽는다 —
  // 아니면 기록을 읽을 자리가 없다.
  const currentSession = useMemo<EducationSession | undefined>(
    () =>
      (tbm.educationSessions || []).find(
        (s: EducationSession) => s.id === currentEducationSessionId,
      ),
    [tbm.educationSessions, currentEducationSessionId],
  );

  /**
   * 완료가 **닫으려는 회차** — 서버에 대상을 말하기 위한 값 (F-21, `adapter.complete`의 sessionId).
   *
   * 화면이 확실히 아는 경우에만 채운다: 보고 있는 회차가 `IN_PROGRESS`일 때. 그 밖은 비우고
   * 종전 동작(서버가 활성 회차를 찾는다)에 맡긴다 — 비우는 경우는 아래 `viewingActiveSession`이
   * 「대상이 어긋날 수 없다」고 판정하는 것과 **같은 갈래**다:
   *  • 회차가 막 생성돼 목록에 아직 없음(`!currentSession`) — id를 말하면 **정상 완료가 409로 막힌다**.
   *
   * ⚠️ `currentEducationSessionId`를 그대로 쓰지 않는 이유: 그 값은 **완료된 회차를 열람 중**일
   * 때도 채워져 있다. 그 id를 실어 보내면 서버는 활성 회차와 달라 409를 주는데, 그 상황은 F-20이
   * 화면에서 이미 버튼을 잠가 둔 자리라 여기선 판정 재료로 삼지 않는다.
   */
  const targetSessionId =
    currentSession?.status === 'IN_PROGRESS' ? currentSession.id : undefined;

  /**
   * 이 문서는 **확정**됐는가 — 담당자가 더 쓸 수 없는 상태인가.
   *
   * 서버의 토큰 쓰기 게이트(`assertEducatorWriteAllowed`)와 **같은 공유 술어**를 본다(F-7).
   * 이 화면의 안내·버튼은 전부 이 값을 봐야 한다 — 하나라도 자기 축을 손으로 적으면 그 자리가
   * 서버와 갈려, 없는 버튼을 가리키거나 눌리는데 403이 나는 상태가 된다.
   */
  // 로컬 「종료」(다회 마지막 회차를 방금 완료)도 확정과 같은 잠금이다 — 종전엔 사본 상태를 'APPROVED'로
  // 덮어 같은 술어가 참이 됐다.
  const isFinalized = isTbmEducationFinalized(tbmStatus) || localCompleted;

  // 기록(안전조치 확인/시행 결과/특이사항) 편집 가능 단계 = 미확정.
  const canEditRecords = !isFinalized;

  /**
   * 안전조치 확인의 **예/아니오 판정**만 따로 닫는 축 — 그 회차가 완료(담당자 서명)됐는가.
   *
   * ⚠️ 서술과 판정은 성질이 다르다. 「시행 결과」·「특이사항」·「조치 내용」은 **서술**이라
   * 나중에 보완하는 것이 자연스럽지만, 예/아니오는 **그 시각 현장 상태의 판정**이다. 교육 완료
   * 시점에 담당자 서명이 그 회차에 들어가고, 서명은 「이 회차 기록이 사실이다」를 보증한다 —
   * 서명 뒤에 판정을 바꾸면 **서명이 보증하지 않는 내용이 서명 아래 놓인다.**
   *
   * 전례가 이유를 말한다: 2026-08-31까지 이 표가 `checked: true`로 채워져 prod 37건이 전부
   * all-true·「아니오」 0건이었다(점검 기록으로 기능한 적이 없다는 뜻). 그 반성으로 미선택 기본 +
   * 완료 차단을 넣었는데, **완료 후 판정 수정이 열려 있으면 같은 구멍의 다른 형태**다 — 완료
   * 때 정직하게 아니오를 골랐다가 나중에 예로 바꿀 수 있다.
   *
   * ⚠️ **이 잠금에는 «되돌릴 출구가 없다»**(2026-09-11 사용자 확인). 다회차의 「교육 시작」은
   * **새 회차**를 만들 뿐 완료된 회차를 다시 열지 않는다(`showStartButton` → `handleStartEducation`).
   * 그래서 안내 문구에 「회차를 재시작하세요」류를 **적지 않는다** — 화면에 없는 동작을 가리키는
   * 안내는 막다른 길보다 나쁘다. 사유만 말한다.
   *
   * 판정 대상은 **고른 회차**다(문서 전체가 아니다) — 완료된 1회차를 보면서 진행 중인 2회차를
   * 체크하는 일이 없도록.
   */
  const safetyCheckDecisionLocked = currentSession?.status === 'COMPLETED';

  /**
   * **화면이 보고 있는 회차 = 액션이 작용하는 회차**인가 (F-20).
   *
   * 「직접 서명 받기」와 「교육 완료」는 고른 회차가 아니라 **진행 중 회차**에 작용한다:
   * 서명은 `qrSession.token`으로 붙고(QR 세션은 진행 중 회차의 것), 완료는 세션 id를 보내지 않아
   * 서버가 진행 중 세션을 찾는다. 그래서 다른 회차를 보는 중에 누르면 **대상이 어긋난다**:
   *   - 직접 서명: 서명은 진행 중 회차로 가는데 화면은 고른 회차의 참석자만 그리므로 **방금 받은
   *     서명이 안 보인다** → 안 들어간 줄 알고 또 받게 된다.
   *   - 교육 완료: 더 나쁘다. `complete`에 함께 실어 보내는 `safetyChecks`·`specialNotes`가
   *     **화면 state = 고른 회차의 값**이라, **완료된 1회차의 기록이 진행 중 2회차에 저장된다.**
   *     미선택 차단(`blockOnUnselectedSafetyChecks`)도 고른 회차를 검사하므로 **우회된다**.
   *
   * ⚠️ 이 조합은 **2026-09-11 F-18을 고치면서 새로 열린 상태 공간**이다. 그 전에는 활성 세션
   * 추적이 IN_PROGRESS를 상태로 강제해 「보는 회차 ≠ 진행 중 회차」가 성립할 수 없었다.
   * 잠금을 푸는 것(회차 전환)과 그 자유가 만든 새 조합을 닫는 것은 **짝**이다.
   *
   * 막 생성돼 목록에 아직 없는 세션(refetch 대기)은 진행 중으로 본다 — 활성 세션 추적 effect의
   * race 처리와 같은 판단이고, 안 그러면 교육 시작 직후 두 버튼이 잠깐 사라진다.
   */
  const viewingActiveSession = !currentSession || currentSession.status === 'IN_PROGRESS';

  /** 진행 중 회차의 번호 — 안내가 「어디로 가야 하나」를 말할 수 있게. 목록은 최신 우선이다. */
  const activeSessionLabel = useMemo(() => {
    const sessions = tbm.educationSessions || [];
    const idx = sessions.findIndex((s: EducationSession) => s.status === 'IN_PROGRESS');
    return idx === -1 ? null : `${sessions.length - idx}회차`;
  }, [tbm.educationSessions]);

  /**
   * 교육 현장 기록(사진·메모)을 담당자가 더할 수 있나 — 서버 `addEducationEntryByToken` 등의 짝.
   *
   * F-7: 종전 술어는 `!CANCELLED && (!COMPLETED || 다회차 기간 내)`라, **확정된 다회차 문서**가
   * 교육 기간 안이면 추가·삭제를 계속 허용했다(그리고 그때는 서버도 CANCELLED만 봤으므로 실제로
   * 써졌다). 확정본은 법적 증빙이라 편집 불가이고(결정 `2026-06-11-report-chrome-layout`),
   * 교육 기간은 *언제까지 진행하나*를 답할 뿐 *이 문서가 닫혔나*를 답하지 않는다 — 그래서 기간
   * 예외를 걷어내고 서버 게이트와 같은 축 하나만 본다. `PENDING_APPROVAL`이 함께 닫히는 것도
   * 이 수정분이다(결재자가 보는 내용이 발밑에서 바뀌면 안 된다).
   */
  const canWriteEducationEntries = !isFinalized;

  // 저장 대상이 존재하는지 — 저장 대상 세션(IN_PROGRESS 또는 완료-미확정)이 있을 때
  const hasSavableSession = !!currentSession;

  // 선택된 세션의 저장값으로 로컬 입력 state 초기화
  //  - 완료된 세션을 열면 그 값이 보이고 편집 가능
  //  - IN_PROGRESS 진입(QR) 시 안전조치 확인은 별도 effect가 기준 목록으로 보강
  //  - 세션 id가 바뀔 때만 재초기화 (tbm 재조회/폴링으로 인한 입력 중 덮어쓰기 방지)
  // 초기값을 sentinel(undefined)로 두어 첫 마운트에서 effect 본문이 반드시 실행되게 한다.
  // (세션 없음 시 currentEducationSessionId=null 이라 null 초기값이면 첫 실행이 조기 return 되어
  //  else 분기의 기준목록 보강이 누락됐다.) 이후 같은 값이면 skip(폴링 덮어쓰기 방지)은 유지.
  const initializedSessionIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (currentEducationSessionId === initializedSessionIdRef.current) return;
    initializedSessionIdRef.current = currentEducationSessionId;
    if (currentSession) {
      setSafetyChecks(
        currentSession.safetyChecks?.length ? currentSession.safetyChecks : safetyCheckBase,
      );
      setSafetyCheckNotes(currentSession.safetyCheckNotes || '');
      setSpecialNotes(currentSession.specialNotes || '');
      setRecordsSavedAt(null);
    } else {
      // 교육 시작 전(세션 없음): 안전조치 확인은 작성자 정의 위험요인(기준 목록)으로 채워
      // 교육담당자가 회차마다 예/아니오를 체크하게. 시행결과/특이사항 staging은 유지.
      // 입력 중 덮어쓰기 방지 위해 비어 있을 때만 보강.
      setSafetyChecks((prev) => (prev.length > 0 ? prev : safetyCheckBase));
    }
    // 프로그램적 로드는 자동저장 트리거가 아니다 — dirty 리셋.
    recordsDirtyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEducationSessionId, currentSession]);

  // 세션 기록 자동저장 (디바운스 1200ms).
  // 사용자 편집(dirty)일 때만, 미확정 시 PATCH.
  //  - 세션 있음: 기존 updateSessionRecords(sessionId, ...) 경로.
  //  - 세션 없음(교육 시작 전): upsertSessionRecords 어댑터가 있으면(토큰 educator)
  //    세션을 lazy 생성+저장하고, 반환 id로 currentEducationSessionId를 세팅한다.
  //    이때 init effect가 빈 세션으로 로컬을 덮지 않게 ref를 그 id로 선갱신한다
  //    (handleStartEducation의 carry-over 패턴과 동일).
  //  - 둘 다 불가(인증 페이지 + 세션 없음)면 스킵 — 시작 시 carry-over로 처리.
  useEffect(() => {
    if (!recordsDirtyRef.current) return;
    if (!canEditRecords) return;
    // 세션 기반 PATCH(또는 lazy upsert). 어댑터 미지원이면 스킵(complete 시 carry-over).
    if (!currentEducationSessionId && !adapter.upsertSessionRecords) return;
    if (currentEducationSessionId && !adapter.updateSessionRecords) return;
    const updateSessionRecords = adapter.updateSessionRecords;
    const upsertSessionRecords = adapter.upsertSessionRecords;
    const t = setTimeout(async () => {
      try {
        const recordsPayload = {
          safetyChecks: safetyChecks.length > 0 ? safetyChecks : undefined,
          safetyCheckNotes,
          specialNotes,
        };
        if (currentEducationSessionId) {
          await updateSessionRecords!(currentEducationSessionId, recordsPayload);
        } else {
          // 교육 시작 전 lazy 생성 — 반환 id를 staging 보존하며 반영.
          const { educationSessionId } = await upsertSessionRecords!(recordsPayload);
          if (educationSessionId) {
            initializedSessionIdRef.current = educationSessionId;
            setCurrentEducationSessionId(educationSessionId);
          }
        }
        recordsDirtyRef.current = false;
        setRecordsSavedAt(Date.now());
      } catch {
        // 저장 실패는 무시 — 다음 변경 때 재시도(dirty 유지)
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safetyChecks, safetyCheckNotes, specialNotes, currentEducationSessionId, canEditRecords]);

  // ── 라이브 동기 (DT8) ───────────────────────────────────────────────
  // 교육담당자↔안전관리자가 같은 다회차 교육을 동시에 열었을 때, 상대가 저장한
  // 세션 기록을 새로고침 없이 반영한다. pollAttendees(참석자/서명)와 별개로
  // TBM 전체를 주기 재조회한다.

  // 1) 주기적 TBM 재조회 — 다회차 + 미확정(canEditRecords)일 때 5초 간격 onRefresh().
  //    QR/IN_PROGRESS 조건 없이도 동작(교육 시작 전 상대의 lazy 세션 생성 반영 위해).
  const livePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!isMultiSession || !canEditRecords) return;
    livePollRef.current = setInterval(() => {
      onRefresh();
    }, 5000);
    return () => {
      if (livePollRef.current) clearInterval(livePollRef.current);
    };
  }, [isMultiSession, canEditRecords, onRefresh]);

  // 2) 활성 세션 추적 — 활성(IN_PROGRESS) 세션이 **새로 나타나면 한 번** 그 회차로 전환한다.
  //    막 생성돼 목록에 아직 없는 세션(refetch 대기)은 되돌리지 않는다(race 방지).
  //    단, 편집 중(dirty)이면 입력 보존을 위해 전환하지 않는다.
  //
  //    ⚠️ **「항상 우선」이 아니다 — 자동 전환은 «전이»에 붙지 «상태»를 강제하지 않는다**(F-18).
  //    종전 술어는 매 실행마다 IN_PROGRESS로 되돌렸는데, deps에 `currentEducationSessionId`가
  //    있어 사용자가 교육 이력 select로 다른 회차를 고르면 **같은 tick에 되돌아왔다**. 화면에서는
  //    「select가 안 먹는다」로 보이고(값이 되돌아오니 프로브 넷 — 마우스·CDP·native setter·
  //    onChange 직접 호출 — 이 전부 무반응이었다), 진행 중인 회차가 있는 한 **완료된 회차를 고를
  //    방법이 없어** 그 회차의 기록·참석자와 「보고서 미리보기」(선택 회차 COMPLETED 조건)가
  //    통째로 막혔다. 5초 라이브 폴링이 effect를 계속 깨우므로 되돌림은 영구적이었다.
  //    (같은 계열: memory `feedback-state-vs-event-do-not-share-one-flag`)
  const autoSwitchedSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isMultiSession) return;
    if (recordsDirtyRef.current) return; // 편집 중이면 전환 안 함(입력 보존)
    const sessions = tbm.educationSessions || [];
    const inProgress = sessions.find((s: EducationSession) => s.status === 'IN_PROGRESS');
    if (inProgress) {
      // 그 활성 세션으로 «아직 전환한 적이 없을 때»만 전환한다. 이미 전환했다면 그 뒤 사용자가
      // 고른 회차를 존중한다 — 다음 자동 전환은 *다른* 세션이 IN_PROGRESS가 될 때(새 회차 시작).
      if (autoSwitchedSessionIdRef.current !== inProgress.id) {
        autoSwitchedSessionIdRef.current = inProgress.id;
        if (inProgress.id !== currentEducationSessionId) setCurrentEducationSessionId(inProgress.id);
      }
      return;
    }
    // 활성 세션이 사라졌다(완료/취소). 다음에 열리는 회차는 «새 전이»이므로 표식을 비운다.
    autoSwitchedSessionIdRef.current = null;
    // 활성 세션 없음: currentEducationSessionId가 null일 때만 최신으로.
    // (현재 id가 있으나 목록에 아직 없는 경우=막 생성된 세션 refetch 대기 → 되돌리지 않음)
    if (!currentEducationSessionId && sessions.length > 0) {
      setCurrentEducationSessionId(sessions[0].id);
    }
  }, [isMultiSession, tbm.educationSessions, currentEducationSessionId]);

  // 3) 기록 라이브 반영 — currentSession의 기록 필드가 갱신되면, 편집 중(dirty)이
  //    아닐 때만 로컬 입력 state에 반영. 프로그램적 set이므로 dirty를 true로
  //    만들지 않는다(핸들러에서만 true) → 자동저장 effect는 dirty=false라 즉시
  //    return(무한루프/역저장 없음). init effect(세션 *전환* 키)와 공존:
  //    이 effect는 같은 세션의 *기록 갱신* 시 동작하며, 양쪽 모두 dirty 가드.
  useEffect(() => {
    if (!currentSession) return;
    if (recordsDirtyRef.current) return; // 편집 중이면 상대 변경으로 덮지 않음
    setSafetyChecks(
      currentSession.safetyChecks?.length ? currentSession.safetyChecks : safetyCheckBase,
    );
    setSafetyCheckNotes(currentSession.safetyCheckNotes || '');
    setSpecialNotes(currentSession.specialNotes || '');
  }, [
    currentSession?.safetyChecks,
    currentSession?.safetyCheckNotes,
    currentSession?.specialNotes,
    currentSession,
    safetyCheckBase,
  ]);

  // 사진 정규화 (상세 페이지와 동일 로직). 입력은 문서 파생이라 COMPOSED 사진 행도 그대로 탄다
  // — 그쪽은 이미 findings를 가진 신 형식이므로 아래 옛 형식 보정에 걸리지 않는다.
  const normalizedPhotos = useMemo((): TBMPhoto[] => {
    const raw = doc.photos;
    if (raw.length === 0) return [];

    const hasOldFormat = raw.some((p: any) => !p.findings && p.description);
    if (!hasOldFormat) return raw;

    const hazards = doc.hazards;
    const measures = doc.safetyMeasures;
    const measuresPerHazard = hazards.length > 0
      ? Math.ceil(measures.length / hazards.length)
      : measures.length;
    const hazardMeasuresMap = new Map<string, string[]>();
    hazards.forEach((h, i) => {
      const start = i * measuresPerHazard;
      const end = Math.min(start + measuresPerHazard, measures.length);
      hazardMeasuresMap.set(h, measures.slice(start, end));
    });

    const map = new Map<string, TBMPhoto>();
    for (const p of raw) {
      const desc = (p as any).description as string | undefined;
      const existing = map.get(p.photoId);
      const matchedMeasures = desc ? hazardMeasuresMap.get(desc) : undefined;
      const finding = desc
        ? { description: desc, measures: matchedMeasures && matchedMeasures.length > 0 ? matchedMeasures : undefined }
        : undefined;
      if (existing) {
        if (finding) {
          existing.findings = [...(existing.findings || []), finding];
        }
      } else {
        map.set(p.photoId, {
          photoId: p.photoId,
          photoUrl: p.photoUrl,
          source: p.source,
          caption: p.caption,
          findings: finding ? [finding] : undefined,
        });
      }
    }
    return Array.from(map.values());
  }, [doc.photos, doc.hazards, doc.safetyMeasures]);

  // 중점위험요인 매칭용 헬퍼
  const ch = doc.criticalHazard;
  const chText = ch?.hazard || '';
  // 정본은 `lib/tbm`(spec `lib/__tests__/tbm-utils.test.ts`) — 종전 인라인 복제를 걷었다(라운드 5 W-p).
  const chMeasureSet = useMemo(() => buildCriticalHazardMeasureSet(ch), [ch]);

  // 동적 번역 수집기는 구 content 키를 구조적으로 읽으므로 **파생을 그 형상으로 넘긴다** —
  // tbm을 그대로 주면 COMPOSED에서 위험요인·대책·중점위험요인이 통째로 번역 대상에서 빠진다.
  const dynamicTexts = useMemo(
    () =>
      collectTbmDynamicTexts(
        {
          title: tbm.title,
          content: {
            workType: doc.workType,
            workDescription: doc.workDescription,
            specialNotes: doc.savedSpecialNotes,
            hazards: doc.hazards,
            safetyMeasures: doc.safetyMeasures,
            hazardItems: doc.hazardItems,
            criticalHazard: doc.criticalHazard,
          },
        },
        normalizedPhotos,
      ),
    [tbm.title, doc, normalizedPhotos],
  );

  const { getTranslated } = useDynamicTranslation(dynamicTexts);

  // 하단 액션바 표시 여부 (REJECTED만 제외 — 구 CREATED/REVIEWING/IN_PROGRESS는 DRAFT로 합쳐졌다).
  // ⚠️ 확정도 **표시**한다 — 확정일 때 이 자리가 담는 것은 버튼이 아니라 「교육이 종료되었습니다」
  //    계열의 잠금 안내라, 숨기면 담당자가 왜 아무것도 못 하는지 알 길이 없어진다(F-7).
  const showBottomAction = tbmStatus === 'DRAFT'
    || isFinalized
    || (isMultiSession && isWithinEducationPeriod); // 다회 교육 기간 내에는 항상 표시

  // 1회성은 완료해도 서버 status가 DRAFT라, 완료 판단은 EducationSession COMPLETED 존재로 한다.
  const isSingleCompleted =
    !isMultiSession && (tbm.educationSessions || []).some((s) => s.status === 'COMPLETED');

  // 교육 시작 버튼 표시 조건
  // 단일회차도 완료 세션이 있어도 확정 전(CREATED)이면 재시작 가능 —
  // 2026-09-03 단일회차 재시작 정책(서버도 완료 시 토큰을 더 이상 비활성화하지 않음).
  // 확정(COMPLETED/PENDING_APPROVAL/CANCELLED)은 status 조건이 이미 배제한다.
  //
  // ⚠️ `!qrSession`은 구 `(IN_PROGRESS && !qrSession)` 절을 옮겨 온 것이다. 진행 중 세션이
  // 있으면 문서 상태가 `SIGNING`이라 구 조건의 첫 두 항에 안 걸렸는데, 그 상태가 사라져
  // 지금은 진행 중에도 `CREATED`로 온다 — 가드를 빼면 교육 중에 「교육 시작」이 뜬다.
  // (2026-09-08 `tbm-status-model-two-axes`: 활동 국면은 문서 enum이 아니라 세션이 답한다.)
  //
  // ⚠️ 다회차는 **기간 안에서만**(F-36, 2026-09-12 흐름 6 실측): 종료일이 지난 문서에서 버튼이
  // 그대로 떠 A가 새 회차를 열 수 있었다 — 그 아래엔 「교육 기간이 종료되었습니다」가 떠 있는
  // 채로. 서버(`createQRSession`)도 같은 게이트를 갖게 됐지만, 누르면 400이 나는 버튼을 두는 것은
  // 막다른 길이라 화면도 같은 축으로 숨긴다. 단일회차(기간 없음)는 종전 그대로.
  const showStartButton =
    tbmStatus === 'DRAFT' &&
    !localCompleted &&
    !qrSession &&
    (!isMultiSession || isWithinEducationPeriod);

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* 헤더 (상단 고정) */}
      <div className="sticky top-0 z-30 bg-brand-600 text-white px-4 py-3 shadow">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1 text-xs text-white/90 hover:text-white mb-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                상세
              </button>
            )}
            <p className="text-xs opacity-80">SafetyHub 안전교육</p>
            {(() => {
              const siteName = tbm.site?.name || '';
              const titleText = tbm.title || '';
              const dashIdx = siteName && titleText.startsWith(siteName) ? siteName.length : -1;
              if (dashIdx > 0) {
                const rest = titleText.slice(dashIdx).replace(/^\s*[-–]\s*/, '').trim();
                return (
                  <>
                    <h1 className="text-lg font-bold truncate">{siteName}</h1>
                    {rest && <p className="text-base opacity-80 truncate">{rest}</p>}
                  </>
                );
              }
              return <h1 className="text-lg font-bold truncate">{titleText}</h1>;
            })()}
            <p className="text-sm opacity-80">{educatorName} 담당자</p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <LanguageSwitcher className="[&_button]:bg-white/20 [&_button]:border-white/40 [&_button]:text-white [&_button]:hover:bg-white/30" dropUp={false} />
            {/* QR 버튼 + 서명 현황 (QR 세션 활성 시) */}
            {qrSession && (
              <div className="flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowQR(true)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-card transition-colors"
                  title="QR 코드 보기"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75H16.5v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
                  </svg>
                </button>
                {totalAttendees > 0 && (
                  <span className="text-xs font-medium bg-white/20 px-1.5 py-0.5 rounded-full">
                    {signedCount}/{totalAttendees}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`flex-1 max-w-lg mx-auto w-full p-4 space-y-4 ${showBottomAction ? 'pb-24' : ''}`}>
        {/* 상태 배지 + 내용 수정 */}
        <div className="flex items-center gap-2">
          {/* 로컬 「종료」는 배지 어휘만 확정본의 것을 빌린다(종전 표시 그대로) — 판정은 `localCompleted`가 든다. */}
          <Badge tone={getTBMStatusBadgeTone(localCompleted ? 'APPROVED' : tbmStatus)}>
            {getTBMStatusLabel(localCompleted ? 'APPROVED' : tbmStatus, locale)}
          </Badge>
          {(() => { const ts = getTBMTagStyle(doc.tag, locale); return ts ? <Badge tone={ts.tone}>{ts.label}</Badge> : null; })()}
          <span className="text-sm text-ink-500">{tbm.site?.name}</span>
        </div>

        {/* 에러 */}
        {error && (
          <div className="bg-red-50 p-3 rounded-card">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* 교육 이력 (다회 교육 시) */}
        {isMultiSession && (
          <Card radius="lg" elevation="none">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-ink-900">교육 이력</h3>
              <span className="text-xs text-ink-500">
                기간: {new Date(tbm.educationStartDate!).toLocaleDateString('ko-KR')} ~{' '}
                {new Date(tbm.educationEndDate!).toLocaleDateString('ko-KR')}
              </span>
            </div>

            {(tbm.educationSessions || []).length === 0 ? (
              <p className="text-xs text-ink-400 text-center py-2">아직 진행된 교육 세션이 없습니다.</p>
            ) : (
              <>
                <select
                  value={currentEducationSessionId || ''}
                  onChange={(e) => setCurrentEducationSessionId(e.target.value || null)}
                  className="w-full px-3 py-2 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                >
                  {(tbm.educationSessions || []).map((session: EducationSession, idx: number) => {
                    const signedCount = session.attendees.filter((a) => a.signedAt).length;
                    const sessionNum = (tbm.educationSessions || []).length - idx;
                    const statusText = session.status === 'COMPLETED' ? '완료' : '진행중';
                    return (
                      <option key={session.id} value={session.id}>
                        {sessionNum}회차 · {new Date(session.sessionDate).toLocaleDateString('ko-KR')} · 참석자 {signedCount}명 · {statusText}
                      </option>
                    );
                  })}
                </select>
                {/* 완료 회차의 「보고서 미리보기」 링크(`/reports/:id/print-preview?…&sessionId=`)는 이 자리에
                    **없다** — 2026-09-11 사용자 결정 「약속을 못 지키면 링크를 내지 않는다」. 이 링크는
                    회차 선택 select 바로 옆이라 문맥이 「고른 회차」를 약속하는데, Composer 문서의 인쇄
                    화면은 `sessionId`를 읽지 않아 문서 전체(다른 회차의 참석자 명단·서명·결재란)가 나온다.
                    교육담당자는 토큰 진입(비로그인)이라 협력업체 담당자가 남의 회차 인원을 보게 된다.
                    정본 정책은 `docs/decisions/2026-03-16-tbm-session-report-access.md`(「세션별 개별
                    보고서」). 인쇄 화면이 회차 필터를 갖는 날 여기에 링크를 되살린다. */}
              </>
            )}

            {/* 「진행 중이 아닐 때」의 판정 축이 문서 상태에서 QR 세션으로 옮겨졌다
                (구 `tbmStatus !== 'IN_PROGRESS'`, 2026-09-08 `tbm-status-model-two-axes`).

                ⚠️ `!isFinalized`가 **F-7 수정분**이다. 이 두 안내는 확정 축을 안 봐서, 확정본에서
                없는 버튼을 가리키고(그 버튼의 게이트 `showStartButton`은 확정을 이미 배제한다)
                재시작이 가능한 것처럼 말했다 — 아래 액션바의 「교육이 종료되었습니다」와 정반대
                말이 한 화면에 같이 떠 있었다. */}
            {!isFinalized && !hasTodaySession && isWithinEducationPeriod && !qrSession && (
              <p className="text-xs text-brand-600 mt-2">
                아래 "교육 시작" 버튼을 눌러 오늘 교육을 진행하세요.
              </p>
            )}
            {!isFinalized && hasTodaySession && !hasTodayInProgressSession && isWithinEducationPeriod && (
              <p className="text-xs text-green-600 mt-2">
                오늘 교육이 완료되었습니다. 필요시 다시 시작할 수 있습니다.
              </p>
            )}
          </Card>
        )}

        {/* TBM 정보 카드 */}
        <Card radius="lg" padding="none" elevation="md" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-line">
            <h2 className="text-base font-semibold text-ink-900">TBM 정보</h2>
          </div>
          <div className="px-4 py-3">
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs font-medium text-ink-500">현장</dt>
                <dd className="mt-0.5 text-sm text-ink-900">{tbm.site?.name || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-500">생성일</dt>
                <dd className="mt-0.5 text-sm text-ink-900">
                  {new Date(tbm.createdAt).toLocaleString('ko-KR', {})}
                </dd>
              </div>
              {(doc.workDescription || doc.workType) && (
                <div className="col-span-2">
                  <dt className="text-xs font-medium text-ink-500">작업내용</dt>
                  <dd className="mt-0.5 text-sm text-ink-900">{doc.workDescription || doc.workType}</dd>
                </div>
              )}
              {/* ⚠️ 현장 주소로 폴백하지 않는다(F-17). 「TBM 장소」는 2026-09-05 결정 이후
                  *현장 안의 어디인가*(A동·B동류 선택지)를 묻는 칸이고, 현장 주소는 그 답이
                  아니다. 폴백은 구 TBM 시절(2026-06-12) 것으로 작성 폼의 prefill이 없어질 때
                  같이 안 닿았다. 회의록 셀은 `bindingKey: 'tbmLocation'`만 읽어 값이 없으면
                  정직하게 비는데 이 화면만 주소를 채워, 같은 라벨이 두 화면에서 다른 것을
                  가리켰다(「TBM 정보엔 장소가 있는데 회의록엔 없다」). 값이 없으면 이 행은
                  나오지 않는 것이 맞다 — 빈 값을 지어내지 않는 규약과 같은 자리다. */}
              {doc.tbmLocation && (
                <div className="col-span-2">
                  <dt className="text-xs font-medium text-ink-500">TBM 장소</dt>
                  <dd className="mt-0.5 text-sm text-ink-900">{doc.tbmLocation}</dd>
                </div>
              )}
            </dl>
            {/* TBM 담당자 — 구 문서 전용(현행 유지). 진행자 정본 체인의 마지막 층이 content.leader고,
                COMPOSED엔 그 키가 없어 자연히 비운다(헤더에 adapter.educatorName이 이미 있다).
                체인 순서 재배열은 구 TBM 동작 변경이라 별건이다(결정 2026-09-08 「착수 중 확인」). */}
            {doc.leader && (
              <div className="mt-3 bg-brand-50 border border-brand-200 rounded-card p-3">
                <h3 className="text-xs font-medium text-brand-800 mb-1.5">TBM 담당자</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {doc.leader.department && (
                    <div>
                      <span className="text-brand-600 font-medium">소속:</span>{' '}
                      <span className="text-ink-900">{doc.leader.department}</span>
                    </div>
                  )}
                  {doc.leader.position && (
                    <div>
                      <span className="text-brand-600 font-medium">직책:</span>{' '}
                      <span className="text-ink-900">{doc.leader.position}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-brand-600 font-medium">성명:</span>{' '}
                    <span className="text-ink-900">{doc.leader.name}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ── 교육 기록: 안전조치 확인 / 시행 결과 / 특이사항 (상시 편집) ── */}
        {/* 단일/다회차 무관하게 교육담당자가 교육 화면에서 입력한다. 저장 축은 회차(EducationSession)
            하나다 — 시작 전·진행중·완료-미확정 모두 입력 가능. */}

        {/* 작업 전 안전조치 확인 */}
        {(canEditRecords ? (
          <Card radius="lg" elevation="none">
            <h3 className="text-sm font-semibold text-ink-900 mb-2">작업 전 안전조치 확인</h3>
            {/* ⚠️ 사유만 말하고 출구를 지어내지 않는다 — 완료된 회차를 다시 여는 수단은 없다
                (다회차의 「교육 시작」은 새 회차를 만든다). 조치 내용은 계속 적을 수 있으므로
                「무엇이 잠겼고 무엇이 열려 있는지」를 한 줄로 가른다. */}
            {safetyCheckDecisionLocked && safetyChecks.length > 0 && (
              <p className="text-xs text-ink-500 mb-2">
                완료된 회차입니다 — 예/아니오는 교육 담당자 서명과 함께 확정됩니다. 조치 내용은
                계속 보완할 수 있습니다.
              </p>
            )}
            {safetyChecks.length === 0 ? (
              <p className="text-xs text-ink-400">위험요인이 없습니다.</p>
            ) : (
              <div className="space-y-2">
                {safetyChecks.map((check, idx) => (
                  <div
                    key={idx}
                    ref={(el) => {
                      safetyCheckRowRefs.current[idx] = el;
                    }}
                    // 강조는 **테두리 색만** 바꾼다 — 두께가 바뀌면 그 순간 행 높이가 밀려
                    // 스크롤이 방금 맞춘 자리에서 어긋난다(레이아웃 중립).
                    className={`border rounded-card p-3 space-y-2 transition-colors ${
                      unselectedFocus?.idx === idx
                        ? 'border-red-400 bg-red-50'
                        : 'border-line-soft'
                    }`}
                    data-unselected-focus={unselectedFocus?.idx === idx ? 'true' : undefined}
                  >
                    <p className="text-sm text-ink-700">{check.hazard}</p>
                    {/* 미선택 안내가 라디오와 한 줄에 서지만 길어질 수 있다 — 한글은 중간에서
                        안 잘리므로 wrap을 열어 둔다(안 열면 라디오가 밀려난다). */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <label
                        className={`flex items-center gap-1 ${
                          safetyCheckDecisionLocked ? 'cursor-default' : 'cursor-pointer'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`educate-safety-check-${idx}`}
                          checked={check.checked === true}
                          disabled={safetyCheckDecisionLocked}
                          onChange={() => updateSafetyCheck(idx, 'checked', true)}
                          className="h-3.5 w-3.5 text-brand-600 border-ink-300 focus:ring-brand-500 disabled:opacity-60"
                        />
                        <span className="text-xs text-ink-600">예</span>
                      </label>
                      <label
                        className={`flex items-center gap-1 ${
                          safetyCheckDecisionLocked ? 'cursor-default' : 'cursor-pointer'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`educate-safety-check-${idx}`}
                          checked={check.checked === false}
                          disabled={safetyCheckDecisionLocked}
                          onChange={() => updateSafetyCheck(idx, 'checked', false)}
                          className="h-3.5 w-3.5 text-brand-600 border-ink-300 focus:ring-brand-500 disabled:opacity-60"
                        />
                        <span className="text-xs text-ink-600">아니오</span>
                      </label>
                      {/* ⚠️ 「조치를 못 했으면 '아니오'」를 **여기** 둔다 — 이 안내가 없으면
                          완료가 막힌 사람은 사실이 아닌 '예'를 고르게 된다(`safetyCheckBase.ts`
                          JSDoc). 차단 토스트에서 뺀 문장이 사라진 게 아니라 이 자리로 온 것이고,
                          토스트와 달리 미선택인 동안 **상주**한다. */}
                      {check.checked === undefined && (
                        <span className="text-xs text-red-600">
                          미선택 — 교육 완료 전에 선택해야 합니다. 조치를 못 했으면
                          &lsquo;아니오&rsquo;를 고르고 조치 내용을 적으세요.
                        </span>
                      )}
                    </div>
                    {check.checked === false && (
                      <input
                        type="text"
                        value={check.actionRequired || ''}
                        onChange={(e) => updateSafetyCheck(idx, 'actionRequired', e.target.value)}
                        className="w-full px-3 py-1.5 border border-line rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                        placeholder="필요한 조치 내용을 입력하세요"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : (() => {
          // 확정 후: 안전조치 확인을 읽기 전용으로 표시 — 현재/마지막 세션 또는 완료 staging
          const checks = currentSession?.safetyChecks || completedSafetyChecks;
          return checks && checks.length > 0 ? (
            <Card radius="lg" elevation="none">
              <h3 className="text-sm font-semibold text-ink-900 mb-2">작업 전 안전조치 확인</h3>
              <div className="space-y-2">
                {checks.map((check, idx) => (
                  <div key={idx} className="border border-line-soft rounded-card p-3 space-y-1">
                    <p className="text-sm text-ink-900">{check.hazard}</p>
                    {/* 3상태: 값이 없으면 '미조치'가 아니라 '미확인'이다 — 판정을 지어내지 않는다. */}
                    <p
                      className={`text-xs font-medium ${
                        check.checked === undefined
                          ? 'text-ink-400'
                          : check.checked
                            ? 'text-green-600'
                            : 'text-red-600'
                      }`}
                    >
                      {check.checked === undefined ? '미확인' : check.checked ? '조치 완료' : '미조치'}
                    </p>
                    {check.checked === false && check.actionRequired && (
                      <p className="text-xs text-ink-600">필요 조치: {check.actionRequired}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ) : null;
        })())}

        {/* 작업 전 안전점검 시행 결과 (단일/다회차 공통, 세션 기록) */}
        {canEditRecords ? (
          <Card radius="lg" elevation="none">
            <h3 className="text-sm font-semibold text-ink-900 mb-2">작업 전 안전점검 시행 결과</h3>
            <textarea
              value={safetyCheckNotes}
              onChange={(e) => { setSafetyCheckNotes(e.target.value); setRecordsSavedAt(null); recordsDirtyRef.current = true; }}
              placeholder="작업자 복장 및 보호구 착용 상태, 장비/공구 상태, 기상 조건 등을 기록하세요"
              rows={4}
              className="w-full px-3 py-2 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </Card>
        ) : (() => {
          // 확정 후: 안전점검 시행 결과를 읽기 전용으로 표시 — 현재/마지막 세션 또는 완료 staging
          const notes = currentSession?.safetyCheckNotes || completedSafetyCheckNotes;
          const hasSection = currentSession || completedSafetyCheckNotes;
          return hasSection ? (
            <Card radius="lg" elevation="none">
              <h3 className="text-sm font-semibold text-ink-900 mb-2">작업 전 안전점검 시행 결과</h3>
              {notes ? (
                <p className="text-sm text-ink-900 whitespace-pre-wrap bg-surface-soft rounded-card p-3">{notes}</p>
              ) : (
                <p className="text-sm text-ink-400 italic">미입력</p>
              )}
            </Card>
          ) : null;
        })()}

        {/* 특이사항 (회차별) */}
        {(canEditRecords ? (
          <Card radius="lg" elevation="none">
            <h3 className="text-sm font-semibold text-ink-900 mb-2">특이사항</h3>
            <textarea
              value={specialNotes}
              onChange={(e) => { setSpecialNotes(e.target.value); setRecordsSavedAt(null); recordsDirtyRef.current = true; }}
              placeholder={isMultiSession ? '이번 교육 회차의 특이사항을 기록하세요 (선택)' : '특이사항을 기록하세요 (선택)'}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </Card>
        ) : (() => {
          // 확정 후: 특이사항을 읽기 전용으로 표시 — 현재/마지막 세션 또는 완료 staging
          const notes = currentSession?.specialNotes || completedSpecialNotes;
          return notes ? (
            <Card radius="lg" elevation="none">
              <h3 className="text-sm font-semibold text-ink-900 mb-2">특이사항</h3>
              <p className="text-sm text-ink-900 whitespace-pre-wrap bg-surface-soft rounded-card p-3">{notes}</p>
            </Card>
          ) : null;
        })())}

        {/* 기록 저장 버튼 (상시 저장) — 저장 대상 세션 + updateSessionRecords 어댑터 지원 시 */}
        {canEditRecords && hasSavableSession && !!adapter.updateSessionRecords && (
          <div className="flex items-center justify-end gap-2">
            {recordsSavedAt && (
              <span className="text-xs text-green-600">저장되었습니다</span>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveRecords}
              disabled={isSavingRecords}
            >
              {isSavingRecords ? '저장 중...' : '기록 저장'}
            </Button>
          </div>
        )}

        {/* TBM 내용 (표시 전용 — 편집 갈래를 두지 않는다) */}
        <Card radius="lg" padding="none" elevation="md" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-line">
            <h2 className="text-base font-semibold text-ink-900">TBM 내용</h2>
          </div>
          <div className="px-4 py-3 space-y-3">
            {doc.savedSpecialNotes && (
              <div>
                <h3 className="text-xs font-medium text-ink-500">{t('tbm.specialNotes')}</h3>
                <p className="mt-0.5 text-sm text-ink-900">{getTranslated(doc.savedSpecialNotes)}</p>
              </div>
            )}

            {/* 사진 + 위험요인/대책 통합 표시 */}
            {normalizedPhotos.length > 0 ? (
              <div className="space-y-3">
                {normalizedPhotos.map((photo, idx) => {
                  const isCriticalPhoto = chText && photo.findings?.some((f) => f.description === chText);
                  return (
                    <div key={`${photo.photoId}-${idx}`} className={`border-2 rounded-card overflow-hidden ${isCriticalPhoto ? 'border-red-300' : 'border-line'}`}>
                      <img
                        src={photo.photoUrl}
                        alt={photo.caption || `현장 사진 ${idx + 1}`}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                      <div className="p-3 space-y-2">
                        {photo.caption && (
                          <p className="text-sm font-medium text-ink-700">{getTranslated(photo.caption)}</p>
                        )}
                        {photo.findings && photo.findings.length > 0 && (
                          <div className="divide-y divide-line-soft">
                            {photo.findings.map((finding, fIdx) => {
                              const isCritical = chText && finding.description === chText;
                              const measures = finding.measures || [];
                              const extra = isCritical ? getExtraMeasures(chMeasureSet, measures) : [];
                              return (
                                <div key={fIdx} className={fIdx > 0 ? 'pt-2' : ''}>
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-red-100 text-red-600 flex items-center justify-center">
                                      <WarningIcon className="w-2.5 h-2.5" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                                        <p className="text-xs font-medium text-red-600">{t('manager.hazardFindings')}</p>
                                        {finding.severity && <SeverityBadge severity={finding.severity} />}
                                        {isCritical && <CriticalBadge />}
                                      </div>
                                      <span className="text-sm text-ink-900">{getTranslated(finding.description)}</span>
                                    </div>
                                  </div>
                                  {(measures.length > 0 || extra.length > 0) && (
                                    <div className="flex items-start gap-1.5 mt-1.5 ml-0.5">
                                      <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-green-100 text-green-600 flex items-center justify-center">
                                        <ShieldIcon className="w-2.5 h-2.5" />
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-green-700 mb-0.5">{t('manager.measures')}</p>
                                        <ul className="space-y-0.5">
                                          {measures.map((m, mi) => (
                                            <li key={mi} className={`text-sm ${isCritical && chMeasureSet.has(m) ? 'font-bold text-ink-900' : 'text-ink-700'}`}>{getTranslated(m)}</li>
                                          ))}
                                          {extra.map((m, mi) => (
                                            <li key={`extra-${mi}`} className="text-sm font-bold text-ink-900">{getTranslated(m)}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : doc.hazardItems.length > 0 ? (
              /* hazardItems 기반: 위험요인/대책 1:1 카드 */
              <div>
                <h3 className="text-xs font-medium text-ink-700 mb-2">{t('manager.hazardFindings')} / {t('manager.measures')}</h3>
                <div className="space-y-2">
                  {doc.hazardItems.map((item, idx) => {
                    const isCritical = chText && item.hazard === chText;
                    const itemMeasures = (item.selectedMeasures && item.selectedMeasures.length > 0) ? item.selectedMeasures : item.measure ? [item.measure] : [];
                    const extra = isCritical ? getExtraMeasures(chMeasureSet, itemMeasures) : [];
                    return (
                      <div key={idx} className={`rounded-card overflow-hidden ${isCritical ? 'border-2 border-red-300' : 'border border-line'}`}>
                        {item.photoUrl && (
                          <img
                            src={item.photoUrl}
                            alt={`위험요인 사진 ${idx + 1}`}
                            className="w-full h-40 object-cover"
                            loading="lazy"
                          />
                        )}
                        <div className="p-3">
                        <div className="flex items-start gap-1.5">
                          <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-red-100 text-red-600 flex items-center justify-center">
                            <WarningIcon className="w-2.5 h-2.5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                              <p className="text-xs font-medium text-red-600">{t('manager.hazardFindings')}</p>
                              {item.severity && <SeverityBadge severity={item.severity} />}
                              {isCritical && <CriticalBadge />}
                            </div>
                            <span className="text-sm text-ink-900">{getTranslated(item.hazard)}</span>
                          </div>
                        </div>
                        {(itemMeasures.length > 0 || extra.length > 0) && (
                          <div className="flex items-start gap-1.5 mt-1.5 ml-0.5">
                            <span className="shrink-0 mt-0.5 w-4 h-4 rounded bg-green-100 text-green-600 flex items-center justify-center">
                              <ShieldIcon className="w-2.5 h-2.5" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-green-700 mb-0.5">{t('manager.measures')}</p>
                              <ul className="space-y-0.5">
                                {itemMeasures.map((m, mi) => (
                                  <li key={mi} className={`text-sm ${isCritical && chMeasureSet.has(m) ? 'font-bold text-ink-900' : 'text-ink-700'}`}>{getTranslated(m)}</li>
                                ))}
                                {extra.map((m, mi) => (
                                  <li key={`extra-${mi}`} className="text-sm font-bold text-ink-900">{getTranslated(m)}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* 기존 텍스트 방식 fallback (구 문서 전용 — COMPOSED는 늘 빈 배열) */
              <>
                {doc.hazards.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium text-ink-700">{t('manager.hazardFindings')}</h3>
                    <ul className="mt-1 list-disc list-inside text-sm text-ink-900">
                      {doc.hazards.map((h, i) => (
                        <li key={i}>{getTranslated(h)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {doc.safetyMeasures.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium text-ink-700">{t('tbm.safetyMeasures')}</h3>
                    <ul className="mt-1 list-disc list-inside text-sm text-ink-900">
                      {doc.safetyMeasures.map((m, i) => (
                        <li key={i}>{getTranslated(m)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        {/* 교육 현장 기록 (다회 교육 시 현재 세션의 기록만) */}
        <EducationRecordSection
            entries={isMultiSession && currentEducationSessionId
              ? (tbm.educationEntries || []).filter(e => e.sessionId === currentEducationSessionId)
              : (tbm.educationEntries || [])
            }
            canAdd={canWriteEducationEntries}
            canDelete={canWriteEducationEntries}
            canReorder={canWriteEducationEntries}
            onAddText={async (content) => {
              await adapter.addText(content);
              onRefresh();
            }}
            onAddPhoto={async (files) => {
              for (const file of files) {
                await adapter.uploadPhoto(file);
              }
              onRefresh();
            }}
            onDelete={async (entryId) => {
              await adapter.deleteEntry(entryId);
              onRefresh();
            }}
            onEditText={async (entryId, content) => {
              await adapter.updateText(entryId, content);
              onRefresh();
            }}
            onReorder={async (entries) => {
              await adapter.reorder(entries);
              onRefresh();
            }}
            annotationApi={{
              cacheKey: adapter.annotationCacheKey,
              get: (entryId) => adapter.getAnnotation(entryId),
              save: (entryId, data) => adapter.saveAnnotation(entryId, data),
            }}
        />

        {/* 서명 현황 (현재 세션의 참석자만) */}
        {currentSessionAttendees.length > 0 && (
          <Card radius="lg" padding="none" elevation="md" className="overflow-hidden">
            <div className="px-4 py-3 border-b border-line flex items-center justify-between">
              <h3 className="text-base font-semibold text-ink-900">
                서명 현황{' '}
                <span className="text-brand-600">
                  {signedCount}/{totalAttendees}
                </span>
              </h3>
              {/* QR 진입로는 **화면 헤더 하나**로 모은다 — 그 헤더는 `sticky top-0`이라 이 카드까지
                  스크롤해도 따라오고, 서명 현황 카운터도 거기 함께 있다. 여기 텍스트 링크는 같은
                  모달을 여는 두 번째 자리였다. ⚠️ 헤더 버튼(`title="QR 코드 보기"`)까지 지우면
                  진입로가 0이 된다 — 화면만 보면 「깔끔해졌다」로 보이는 사고라 spec으로 못박았다. */}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-line">
                <thead className="bg-surface-soft">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 w-12">순번</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-ink-500">이름</th>
                    {/* 이 열이 담는 값은 서명 여부다 — 「상태」는 문서·교육 상태로도 읽혀 헷갈린다.
                        이웃 th(순번·이름)가 리터럴이라 여기도 리터럴로 둔다(그 표만 t()를 쓰면 규약이 갈린다). */}
                    <th className="px-4 py-2 text-center text-xs font-medium text-ink-500 w-20">서명</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-line">
                  {currentSessionAttendees.map((a: TBMAttendee, idx: number) => (
                    <tr key={a.id}>
                      <td className="px-4 py-2 text-sm text-ink-500">{idx + 1}</td>
                      <td className="px-4 py-2 text-sm font-medium text-ink-900">{a.name}</td>
                      <td className="px-4 py-2 text-center">
                        {a.signedAt ? (
                          a.signatureData ? (
                            <img src={a.signatureData} alt={`${a.name} 서명`} className="h-6 w-auto mx-auto" />
                          ) : (
                            <span className="text-xs text-green-600 font-medium">{t('badge.signed')}</span>
                          )
                        ) : (
                          <span className="text-xs text-ink-400">{t('badge.waiting')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      </div>

      {/* 액션 버튼 (하단 고정) */}
      {showBottomAction && (
        <div className="sticky bottom-0 z-30 bg-white border-t border-line shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
          <div className="max-w-lg mx-auto px-4 py-3 space-y-2">
            {showStartButton && (
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleStartEducation}
                disabled={isStarting}
              >
                {isStarting ? '시작 중...' : '교육 시작 (QR 생성)'}
              </Button>
            )}

            {/* 구 `tbmStatus === 'IN_PROGRESS' && qrSession` — 앞 항이 뒤 항에서 파생된
                문서 상태였다(2026-09-08 `tbm-status-model-two-axes`).

                ⚠️ `!isFinalized`는 F-7 수정분이다. QR 세션이 열려 있는 채로 문서가 확정되면
                (작성자가 상세에서 제출·완료하면 그럴 수 있다) 이 두 버튼이 그대로 남는데, 서버는
                이제 `completeByEducatorToken`을 거절한다 — 게이트를 안 맞추면 «눌리는데 400»이
                된다. 그리고 그 경로는 종전에 확정본을 DRAFT로 되돌리던 자리다. */}
            {qrSession && !isFinalized && (
              <>
                {/* ⚠️ 두 액션은 «진행 중 회차»에 작용한다(F-20) — 다른 회차를 보는 중이면 대상이
                    어긋나므로 막는다. 숨기지 않고 **비활성 + 사유**로 두는 이유는, 사라지면
                    「왜 없지」가 되고 여기엔 실재하는 출구가 있기 때문이다(위 회차 select). */}
                {!viewingActiveSession && (
                  <p className="text-xs text-ink-500 text-center">
                    지난 회차를 보고 있습니다 — 서명과 완료는
                    {activeSessionLabel ? ` 진행 중인 ${activeSessionLabel}` : ' 진행 중인 회차'}에
                    적용됩니다. 위 교육 이력에서 그 회차를 선택하세요.
                  </p>
                )}
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={() => setShowDirectSign(true)}
                  disabled={!viewingActiveSession}
                >
                  직접 서명 받기
                </Button>
                {/* 서명을 받기 **전에** 미선택을 먼저 알린다 — 서명까지 시키고 거절하면 헛수고가 된다. */}
                <Button
                  variant="success"
                  size="lg"
                  className="w-full"
                  onClick={() => {
                    if (blockOnUnselectedSafetyChecks()) return;
                    setShowSignature(true);
                  }}
                  disabled={isCompleting || !viewingActiveSession || signedCount === 0}
                >
                  {isCompleting ? '완료 처리 중...' : '교육 완료 (담당자 서명)'}
                </Button>
                {/* F-37: 참석자 서명 0명이면 서버가 거절한다(인앱 완료와 같은 규칙) — 서명까지 받고
                    거절당하지 않게 버튼을 잠그고 이유를 말한다. */}
                {viewingActiveSession && signedCount === 0 && (
                  <p className="text-xs text-ink-500" data-testid="complete-needs-attendee">
                    참석자 서명이 1명 이상 있어야 교육을 완료할 수 있습니다.
                  </p>
                )}
              </>
            )}

            {/* 결재/완료 잠금 메시지 (교육기간과 무관하게 표시) — 확정만 잠금.
                단일회차 완료+미확정은 재시작 가능하므로 잠금이 아니라 안내만(2026-09-03 재시작 정책). */}
            {(tbmStatus === 'APPROVED' || localCompleted) && (
              <div className="bg-green-50 rounded-card p-3 text-center">
                <p className="text-green-700 font-medium text-sm">교육이 종료되었습니다.</p>
              </div>
            )}
            {/* ⚠️ `!qrSession`은 F-41 수정분이다. `isSingleCompleted`는 **지난 회차 중 하나라도
                완료됐나**를 묻지 「지금 끝났나」를 묻지 않는다 — 그래서 재시작해서 새 회차가
                도는 중에도 이 안내가 떠, 완료·직접서명 버튼과 「다시 진행할 수 있습니다」가
                한 화면에 함께 있었다. 진행 중에 필요한 것은 안내가 아니라 그 버튼들이다. */}
            {isSingleCompleted && !isFinalized && !qrSession && (
              <div className="bg-green-50 rounded-card p-3 text-center">
                <p className="text-green-700 font-medium text-sm">
                  교육이 완료되었습니다. 필요하면 &lsquo;교육 시작&rsquo;으로 새 세션을 다시 진행할 수 있습니다.
                </p>
              </div>
            )}
            {tbmStatus === 'PENDING_APPROVAL' && (
              <div className="bg-green-50 rounded-card p-3 text-center">
                <p className="text-green-700 font-medium text-sm">교육이 완료되어 결재 대기 중입니다.</p>
              </div>
            )}

            {/* 다회 교육 기간 종료 메시지 — 확정본은 위 잠금 안내가 이미 말하므로 겹쳐 쓰지 않는다.
                ⚠️ 종전 조건은 CANCELLED를 빼지 않아, 취소된 문서에 「교육 기간이 종료되었습니다
                (총 N회 진행)」와 「이 TBM 교육은 취소되었습니다」가 함께 떴다. `isFinalized`로
                수렴하며 그 겹침도 닫힌다(F-7). */}
            {isMultiSession && !isWithinEducationPeriod && !isFinalized && (
              <div className="bg-green-50 rounded-card p-3 text-center">
                <p className="text-green-700 font-medium text-sm">
                  교육 기간이 종료되었습니다.
                  {(tbm.educationSessions || []).length > 0 &&
                    ` (총 ${tbm.educationSessions!.length}회 진행)`}
                </p>
              </div>
            )}

            {tbmStatus === 'CANCELLED' && (
              <div className="bg-red-50 rounded-card p-3 text-center">
                <p className="text-red-700 font-medium text-sm">이 TBM 교육은 취소되었습니다.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR 모달 */}
      {signUrl && (
        <QRModal
          signUrl={signUrl}
          isOpen={showQR}
          onClose={() => setShowQR(false)}
          attendees={currentSessionAttendees}
        />
      )}

      {/* 교육담당자 서명 모달 */}
      {showSignature && (
        <EducatorSignatureModal
          educatorName={educatorName}
          isCompleting={isCompleting}
          onConfirm={handleComplete}
          onCancel={() => setShowSignature(false)}
        />
      )}

      {/* 직접 서명 모달 */}
      {showDirectSign && qrSession && (
        <DirectSignModal
          qrToken={qrSession.token}
          source="educator_proxy"
          onClose={() => setShowDirectSign(false)}
          onSigned={(attendee) => {
            setAttendees(prev => [...prev, attendee]);
          }}
        />
      )}
    </div>
  );
}

// ============================================
// 교육담당자 서명 모달 (TbmEducateView 전용)
// ============================================

function EducatorSignatureModal({
  educatorName,
  isCompleting,
  onConfirm,
  onCancel,
}: {
  educatorName: string;
  isCompleting: boolean;
  onConfirm: (signatureData?: string) => void;
  onCancel: () => void;
}) {
  const { t } = useLocale();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Canvas 이벤트
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const endDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    const signatureData = canvas && hasSignature ? canvas.toDataURL('image/png') : undefined;
    onConfirm(signatureData);
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title="교육 담당자 서명"
      size="md"
      // 완료 처리 중에는 닫기 수단을 전부 잠근다(이관 전 Esc 가드와 같은 의도).
      closeLocked={isCompleting}
      showCloseButton={!isCompleting}
      // 모바일 바텀시트 정렬 유지. base 오버레이의 `items-center p-4`와 같은 속성이라
      // 클래스 나열 순서로는 못 이긴다(Tailwind는 생성 순서로 승부가 갈린다) — `!`로 명시하고
      // sm 이상은 다시 base 기본(중앙 + 여백)으로 되돌린다.
      overlayClassName="!items-end !p-0 sm:!items-center sm:!p-4"
      // 바텀시트일 때 아래 모서리는 화면 밖이라 각지게(이관 전 rounded-t-2xl과 같은 결과).
      panelClassName="rounded-b-none sm:rounded-modal"
      footer={
        <div className="flex w-full gap-2">
          <Button
            type="button"
            className="flex-1"
            onClick={onCancel}
            disabled={isCompleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="success"
            className="flex-1"
            onClick={handleConfirm}
            disabled={isCompleting || !hasSignature}
          >
            {isCompleting ? t('common.loading') : t('sign.signButton')}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-ink-500 mb-3">
        <span className="font-medium text-ink-700">{educatorName}</span> 담당자님, 교육 완료를 확인하는 서명을 해주세요.
      </p>

      {/* 서명 캔버스 */}
      <div className="relative border-2 border-ink-300 rounded-card overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          className="w-full touch-none cursor-crosshair"
          style={{ height: '120px' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={endDrawing}
          onMouseLeave={endDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={endDrawing}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-ink-300 text-sm">여기에 서명해 주세요</p>
          </div>
        )}
      </div>
      {hasSignature && (
        <button
          type="button"
          onClick={clearSignature}
          className="mt-1.5 text-xs text-ink-500 hover:text-red-500"
        >
          서명 지우기
        </button>
      )}
    </Modal>
  );
}

// ============================================
// 공통 아이콘 & 헬퍼 컴포넌트 (TbmEducateView 전용)
// ============================================

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const { t } = useLocale();
  const label =
    severity === 'CRITICAL' ? t('badge.severity.critical') : severity === 'HIGH' ? t('badge.severity.high') : severity === 'MEDIUM' ? t('badge.severity.medium') : t('badge.severity.low');
  // 이전에는 CRITICAL과 HIGH가 둘 다 red였다 — 라벨은 넷인데 색은 셋이라 최상위 두 등급이
  // 색으로 구분되지 않았다. tone 정본을 쓰면서 네 등급이 각자 색을 갖는다.
  return (
    <Badge tone={getFindingSeverityTone(severity)} shape="rounded">
      {label}
    </Badge>
  );
}

function CriticalBadge() {
  const { t } = useLocale();
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-600 text-white text-xs font-bold">
      <WarningIcon className="w-2.5 h-2.5" />
      {t('manager.criticalHazard')}
    </span>
  );
}
